/**
 * Opens Google Maps in a new window or tab centered at a specific address.
 *
 * @param {object} addressInfo - An object containing address information.
 * @param {string} addressInfo.address1 - The first part of the street address.
 * @param {string} addressInfo.address2 - The second part of the street address (optional).
 * @param {string} addressInfo.city - The city.
 * @param {string} addressInfo.state - The state.
 * @param {string} addressInfo.zipCode - The ZIP code.
 * @returns {void}
 *
 * @example
 * // Example usage with two address parts:
 * const addressInfoWithTwoParts = {
 *   address1: '123 Main Street',
 *   address2: 'Apt 4B',
 *   city: 'City',
 *   state: 'State',
 *   zipCode: '12345'
 * };
 * openGoogleMaps(addressInfoWithTwoParts);
 *
 * // Example usage with one address part:
 * const addressInfoWithOnePart = {
 *   address1: '456 Elm Avenue',
 *   city: 'Another City',
 *   state: 'Another State',
 *   zipCode: '67890'
 * };
 * openGoogleMaps(addressInfoWithOnePart);
 */
export function openGoogleLocation(addressInfo: {
  address1: string;
  address2?: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}): void {
  const { address1, address2, city, state, zipCode, country } = addressInfo;

  // Construct the address string
  let addressString = address1;
  if (address2) {
    addressString += `, ${address2}`;
  }
  addressString += `, ${city}, ${state} ${zipCode}, ${country}`;

  // Encode the address for use in a URL
  const encodedAddress = encodeURIComponent(addressString);

  // Construct the Google Maps URL
  const mapsURL = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;

  // Open a new browser window or tab with the Google Maps URL
  window.open(mapsURL, '_blank');
}
